import { CitationSource } from 'types/chat-message'
import { AgentAction, AgentView } from 'types/agent'

const SOURCES_DELIMITER = '<<<SOURCES>>>'
const ACTION_DELIMITER = '<<<ACTION_REQUIRED>>>'
const VIEWS_DELIMITER = '<<<VIEWS>>>'

export type ParsedMessage = {
  text: string
  sources: Record<string, CitationSource> | null
  /** "View result" deep-links emitted after tool runs; null while streaming or absent. */
  views: AgentView[] | null
}

export function parseMessageWithSources(rawMessage: string): ParsedMessage {
  // Views are streamed LAST (after sources), so peel them off first — otherwise the sources JSON
  // tail would include the <<<VIEWS>>> block and fail to parse.
  let working = rawMessage
  let views: AgentView[] | null = null

  const viewsIndex = working.indexOf(VIEWS_DELIMITER)
  if (viewsIndex !== -1) {
    const viewsJson = working.substring(viewsIndex + VIEWS_DELIMITER.length).trim()
    try {
      views = JSON.parse(viewsJson) as AgentView[]
    } catch {
      views = null
    }
    working = working.substring(0, viewsIndex)
  }

  const delimiterIndex = working.indexOf(SOURCES_DELIMITER)

  if (delimiterIndex !== -1) {
    const text = working.substring(0, delimiterIndex).trim()
    const sourcesJson = working.substring(delimiterIndex + SOURCES_DELIMITER.length).trim()

    try {
      const sources = JSON.parse(sourcesJson) as Record<string, CitationSource>
      return { text, sources, views }
    } catch {
      return { text, sources: null, views }
    }
  }

  // Strip partial delimiter during streaming (e.g. "<<<SOUR" / "<<<VIE" at the end)
  const partialMatch = working.match(/\n*<<<[A-Z_>]*$/)
  if (partialMatch && partialMatch.index !== undefined) {
    return { text: working.substring(0, partialMatch.index).trim(), sources: null, views }
  }

  return { text: working, sources: null, views }
}

export function stripCitationMarkers(text: string): string {
  // [N] remote source, [img:N] image, [chart:N] inline chart, [att:N] user-uploaded attachment.
  return text.replace(/\[(?:img:|chart:|att:)?\d+\]/g, '').trim()
}

// Stored upload keys are "<uuid>-<file name>": the uuid only guarantees uniqueness.
const UPLOAD_KEY_PREFIX = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}-/i

/** File name of a stored upload url, without the uniqueness uuid prefix ('' when the url has none). */
export function getStoredFileName(url: string): string {
  return (url.split(/[?#]/)[0].split('/').pop() ?? '').replace(UPLOAD_KEY_PREFIX, '')
}

/** Display name of a document source: the title a tool gave it, else (tools) the stored file name, else its name. */
export function getSourceTitle(source: CitationSource): string {
  const title = source.params?.title
  if (typeof title === 'string' && title.trim()) return title.trim()
  if (source.type !== 'tool' || !source.url) return source.name
  return getStoredFileName(source.url) || source.name
}

// ponytail: regexes, not a markdown parser — enough for one-line truncated previews (chat list).
// Intraword `_` is left alone on purpose (agent names like Analisi_Cliente).
export function stripMarkdown(text: string): string {
  return text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1') // links/images → their text
    .replace(/^[ \t|:*_-]{3,}$/gm, '') // horizontal rules, table separator rows
    .replace(/^[ \t]*(?:(?:>|(?:#{1,6}|[-+*]|\d+[.)])[ \t])[ \t]*)*/gm, '') // quotes, headings, list markers
    .replace(/```\w*|\*+|~~|`/g, '') // code fences, bold/italic, strike, inline code
    .replace(/\|/g, ' ') // table cells
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * One readable line out of a bot message — for the chat list, where control blocks have no
 * business showing up. A message that is only a pending action has no prose at all, and used to
 * preview as `<<<ACTION_REQUIRED>>>{"runId":…}`: fall back to what the action says it does.
 * `parseAgentActions` is below in this file, hence the function declaration hoisting.
 */
export function getPlainMessageText(rawMessage: string): string {
  const { segments, after } = parseAgentActions(rawMessage)

  const spoken = [...segments.map((segment) => segment.text), after]
    .map((part) => stripMarkdown(stripCitationMarkers(part)))
    .filter(Boolean)
    .join(' ')

  return spoken || stripMarkdown(segments[segments.length - 1]?.action?.summary ?? '')
}

export function processCitationMarkers(text: string): string {
  return (
    text
      // Normalize citation markers
      .replace(/\[img:\s*(\d+)\s*\]/g, '[img:$1]')
      .replace(/\[chart:\s*(\d+)\s*\]/g, '[chart:$1]')
      .replace(/\[att:\s*(\d+)\s*\]/g, '[att:$1]')
      .replace(/\[\s*(\d+)\s*\]/g, '[$1]')
      // Collapse 3+ newlines into 2 (max one blank line)
      .replace(/\n{3,}/g, '\n\n')
      // Remove trailing whitespace from lines
      .replace(/[ \t]+$/gm, '')
      // Remove extra spaces (but keep single spaces)
      .replace(/ {2,}/g, ' ')
  )
}

export type AgentActionSegment = {
  /** Conversational text streamed before this action card. */
  text: string
  action: AgentAction
}

export type ParsedAgentMessage = {
  /** One segment per <<<ACTION_REQUIRED>>> block, in order: a resumed run appends the next
   *  pause to the same message, so a message can carry several. Empty when there is none. */
  segments: AgentActionSegment[]
  /** Text after the last action (or the whole message without actions); non-empty => the last
   *  action is resolved and the bot went on. */
  after: string
  /** Citations of every segment, merged: the numbering continues across the resumed streams. */
  sources: Record<string, CitationSource> | null
  views: AgentView[] | null
}

function findJsonEnd(text: string, from: number): number {
  let depth = 0
  let inString = false
  let escaped = false

  for (let i = from; i < text.length; i++) {
    const ch = text[i]
    if (inString) {
      if (escaped) escaped = false
      else if (ch === '\\') escaped = true
      else if (ch === '"') inString = false
      continue
    }
    if (ch === '"') inString = true
    else if (ch === '{') depth++
    else if (ch === '}') {
      depth--
      if (depth === 0) return i + 1
    }
  }
  return -1
}

/**
 * Parses an agentic bot message into { text, action, after }.
 * - No marker: returns the text as-is (a trailing partial marker is stripped so
 *   it never flashes during streaming).
 * - Marker present, JSON incomplete: shows the text before it, no action yet.
 * - Marker + full JSON: returns the parsed action and any continuation (`after`).
 */

const CONTROL_DELIMITERS = [ACTION_DELIMITER, SOURCES_DELIMITER, VIEWS_DELIMITER]

type ControlBlock = { delimiter: string; json: string; before: string; rest: string }

// The next control block of the message: the text before it, its brace-matched JSON and what
// follows. Every resumed stream appends its own sources block, so a message may carry several,
// each glued to the continuation text that comes right after it ("...}Fatto: ...").
function nextControlBlock(raw: string): ControlBlock | null | 'streaming' {
  const hits = CONTROL_DELIMITERS.map((delimiter) => ({ delimiter, at: raw.indexOf(delimiter) })).filter((h) => h.at !== -1)
  if (hits.length === 0) return null

  const { delimiter, at } = hits.reduce((a, b) => (b.at < a.at ? b : a))
  const tail = raw.slice(at + delimiter.length)
  const start = tail.search(/[{[]/)
  const end = start === -1 ? -1 : findJsonEnd(tail, start)
  if (end === -1) return 'streaming'

  return { delimiter, json: tail.slice(start, end), before: raw.slice(0, at), rest: tail.slice(end) }
}

export function parseAgentActions(raw: string): ParsedAgentMessage {
  const segments: AgentActionSegment[] = []
  const sources: Record<string, CitationSource> = {}
  let views: AgentView[] | null = null
  let text = ''
  let rest = raw

  for (;;) {
    const block = nextControlBlock(rest)

    if (block === 'streaming') {
      // The block is still arriving: show the text up to its delimiter and nothing more.
      const at = Math.min(...CONTROL_DELIMITERS.map((d) => rest.indexOf(d)).filter((i) => i !== -1))
      rest = rest.slice(0, at)
      break
    }

    if (block === null) {
      // Strip a partial delimiter forming at the end while streaming.
      const partial = rest.match(/\n*<<<[A-Z_>]*$/)
      if (partial && partial.index !== undefined) rest = rest.slice(0, partial.index)
      break
    }

    text += block.before

    try {
      const parsed = JSON.parse(block.json)
      if (block.delimiter === ACTION_DELIMITER) {
        segments.push({ text: text.trim(), action: parsed as AgentAction })
        text = ''
      } else if (block.delimiter === SOURCES_DELIMITER) {
        Object.assign(sources, parsed)
      } else {
        views = parsed as AgentView[]
      }
    } catch {
      // A block that does not parse is dropped, its text kept.
    }

    rest = block.rest
  }

  return {
    segments,
    after: (text + rest).trim(),
    sources: Object.keys(sources).length > 0 ? sources : null,
    views,
  }
}

/** True when the message ends on an action nobody has answered yet: its JSON is complete and only
 *  control blocks (sources, views) follow it. */
export function endsWithPendingAction(raw: string): boolean {
  const { segments, after } = parseAgentActions(raw)

  return segments.length > 0 && after === ''
}
